import React, { useState, useEffect, useRef } from "react";
import {
  resolveCertLayout,
  resolveCertSize,
  resolveCertX,
  formatCertValue,
  certFont,
  certFamily,
  loadCertFonts,
} from "../utils/certificateLayout";
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMars, faVenus, faFileLines, faChevronDown, faChevronUp, faTimes } from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import { Button } from "@vionixasia/shared-components";
import { getFlagCode } from "@vionixasia/shared-components/lib/countryCodes";
import { displayElapsed } from "../utils/finishTime";
import { lastCheckpoint } from "../utils/lastCheckpoint";

// Milliseconds as H:MM:SS race time (hours keep growing past 24).
const fmtSplit = (ms) => {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str.trim().toLowerCase().split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

/**
 * By default show only chip-timed nodes in the timeline: Start and Finish.
 *
 * Mid-course checkpoints are typed by marshals against a BIB, so a late or
 * missed keystroke produces a timeline that contradicts itself while the
 * official gun time is perfectly correct — BIB 2021 appears to cover her last
 * leg in five minutes, BIB 1147 appeared to start after her own CP1. Start and
 * Finish come off the mat, so they are kept: they are what the time is built
 * from and what a runner checks first.
 *
 * The hidden splits stay in Firestore, the API response and the Excel export,
 * so the operator can still audit them. An event shows them when the admin
 * switches on "Splits" (event_timing/{id}.showSplits).
 */

export const RunnerDetailsModal = ({ runner, onClose, categories, eventId, eventData, rankedTotals }) => {
  const [checkpointOpen, setCheckpointOpen] = useState(false);
  const [liveData, setLiveData] = useState(null);
  const [showSplits, setShowSplits] = useState(false);
  const db = getFirestore();
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Makassar";
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!eventId) return;
    let live = true;
    getDoc(doc(db, "event_timing", eventId))
      .then((snap) => { if (live) setShowSplits(snap.data()?.showSplits === true); })
      .catch(() => {});
    return () => { live = false; };
  }, [db, eventId]);

  useEffect(() => {
    if (!eventId) return;
    // A relay row is a team, and its laps are spread across one document per
    // member. Reading only runner.bib would show a single leg's laps under the
    // team's name — which is why the row used to refuse to open at all.
    const bibs = runner?.isTeam
      ? (runner.memberBibs?.length ? runner.memberBibs : (runner.members || []).map((m) => m.bib))
      : (runner?.bib ? [runner.bib] : []);
    if (!bibs.length) return;

    if (bibs.length === 1) {
      const ref = doc(db, "event_timing", eventId, "results", String(bibs[0]));
      const unsub = onSnapshot(ref, (snap) => { if (snap.exists()) setLiveData(snap.data()); });
      return () => unsub();
    }

    const perMember = new Map();
    const merge = () => {
      const checkpoints = {};
      let completedLoops = 0;
      let lastLoopTimestamp = null;
      perMember.forEach((docData) => {
        Object.entries(docData.checkpoints || {}).forEach(([cpId, cp]) => {
          if (!cp || typeof cp !== "object") return;
          const target = (checkpoints[cpId] = checkpoints[cpId] || {});
          Object.entries(cp).forEach(([key, value]) => {
            if (!/^loop\d+$/.test(key) || !value?.time) return;
            // Key by instant so two members never collide on loop1.
            target[`t${Date.parse(value.time)}`] = value;
          });
        });
      });
      // Renumber the merged crossings chronologically into the loopN shape the
      // rest of the modal reads.
      Object.entries(checkpoints).forEach(([cpId, bag]) => {
        const ordered = Object.entries(bag)
          .map(([key, value]) => ({ ms: Number(key.slice(1)), value }))
          .filter((entry) => Number.isFinite(entry.ms))
          .sort((a, b) => a.ms - b.ms);
        checkpoints[cpId] = Object.fromEntries(
          ordered.map((entry, index) => [`loop${index + 1}`, entry.value])
        );
        completedLoops += ordered.length;
        const last = ordered.at(-1);
        if (last && (!lastLoopTimestamp || last.ms > Date.parse(lastLoopTimestamp))) {
          lastLoopTimestamp = last.value.time;
        }
      });
      setLiveData({ ...runner, checkpoints, completedLoops, lastLoopTimestamp });
    };

    const unsubs = bibs.map((bib) =>
      onSnapshot(doc(db, "event_timing", eventId, "results", String(bib)), (snap) => {
        if (snap.exists()) perMember.set(String(bib), snap.data());
        merge();
      })
    );
    return () => unsubs.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runner?.bib, runner?.isTeam, eventId, db]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  if (!runner) return null;

  const data = liveData || runner;
  const category = categories?.find((c) => c.id === (data.categoryId || runner.categoryId));
  const catName = category?.name?.trim() || data.categoryId || "-";
  const { code: flagCode, display: nationalityDisplay } = getFlagCode(data.nationality || runner.nationality);
  const isLooping = !!category?.isLooping;

  const overallRank = runner.overallRank > 0 ? runner.overallRank : "-";
  const genderRank = runner.genderRank > 0 ? runner.genderRank : "-";

  // "12 of 310" reads as a placing; a bare "12" leaves the runner guessing how
  // big the field was. The denominator counts placings awarded, not entrants —
  // runners with no finish are not part of the standings. Omitted when the
  // total is unknown or the runner has no placing to compare against.
  // Totals are counted over the category the results page loaded; a runner from
  // another category (or an all-categories view) would get someone else's
  // field size, so the denominator is omitted rather than wrong.
  const gk = (runner.gender || "").toLowerCase() === "male" ? "male" : "female";
  const totalsApply = !rankedTotals?.category || rankedTotals.category === runner.categoryId;
  const overallOutOf = runner.overallRank > 0 && totalsApply ? rankedTotals?.overall || 0 : 0;
  const genderOutOf = runner.genderRank > 0 && totalsApply ? rankedTotals?.[gk] || 0 : 0;
  const finishDuration = runner.finishDuration || "-";

  // For looping events: use lastLoopTimestamp (ISO clock time of last loop)
  // For standard events: look in checkpoints.finish.time
  const finishTimeRaw = isLooping
    ? (runner.lastLoopTimestamp || null)
    : (data.checkpoints?.finish?.time || runner.finishTime || null);
  const toMoment = (t) => {
    if (!t) return null;
    if (t.toDate && typeof t.toDate === "function") return moment(t.toDate()).tz(userTimeZone);
    return moment(t).tz(userTimeZone);
  };
  const finishMoment = toMoment(finishTimeRaw);
  const finishTime = finishMoment?.isValid() ? finishTimeRaw : null;
  const arrivalTimeDisplay = finishMoment?.isValid() ? finishMoment.format("HH:mm:ss") : "-";
  const isCOT = runner.isCOT;

  const status = (() => {
    // An operator-set status is a decision, not an inference — it outranks
    // anything derived from the checkpoints. BIB 1282 was marked DNF in the
    // editor but still showed "ON RACE", because having any checkpoint at all
    // was read as "still running" and `status` was never consulted.
    const declared = (data.status || runner.status || "").toUpperCase();
    if (["DNF", "DNS", "DSQ"].includes(declared)) return declared;

    if (isLooping) {
      if (runner.isFinished) return isCOT ? "COT" : "FINISHED";
      // Zero completed loops is not a DNF. The first valid crossing is the
      // baseline (Loop 0), so a runner can legitimately have an active result
      // without Loop 1 yet. DNF/DNS/DSQ must only come from an operator-set
      // status above, never from the loop count.
      if (eventData?.eventStatus === "ongoing") return "ON RACE";
      return runner.completedLoops > 0 ? "FINISHED" : "NO LOOP";
    }
    if (finishMoment?.isValid()) return isCOT ? "COT" : "FINISHED";
    // A recorded duration is a completed run. Buzzer-timed events hold no mat
    // readings at all, so the checkpoint test below declared a finisher DNS —
    // "did not start" — for someone whose time was already on the board.
    if (data.finishDuration || runner.finishDuration) return "FINISHED";
    const hasCP = Object.keys(data.checkpoints || {}).some((k) => data.checkpoints[k]?.time);
    // Past the cut-off nobody is still on the route, so an unfinished runner is
    // no longer "ON RACE" — the race is over and they have no time.
    const cutOff = category?.cutOffTime?.toDate ? category.cutOffTime.toDate() : category?.cutOffTime;
    const cutOffMs = cutOff ? new Date(cutOff).getTime() : NaN;
    // A runner sent off AFTER the cut-off has not failed to finish — the
    // cut-off belongs to an earlier session. On an individual start the clock
    // that matters is their own departure, so a start later than the cut-off
    // means the category time bound no longer describes them.
    const startedAt = data.startMatTime || runner.startMatTime || null;
    const startedMs = startedAt ? new Date(startedAt).getTime() : NaN;
    const startedAfterCutOff =
      Number.isFinite(cutOffMs) && Number.isFinite(startedMs) && startedMs > cutOffMs;
    const raceClosed =
      Number.isFinite(cutOffMs) && Date.now() > cutOffMs && !startedAfterCutOff;
    if (!hasCP) return "DNS";
    return raceClosed ? "DNF" : "ON RACE";
  })();

  const checkpointTimeline = (() => {
    if (isLooping) {
      // runner.loopTimestamps = { loop1: Timestamp, loop2: Timestamp, ... } (raw Firestore timestamps)
      //
      // Length comes only from what the runner actually recorded.
      // A team has no loopTimestamps of its own — the API sends one row per
      // team, and the crossings live on the member documents merged above. Fall
      // back to those so the relay timeline is not silently empty.
      const loopTimestamps = runner.loopTimestamps
        || (runner.isTeam
          ? Object.fromEntries(
              Object.entries(data.checkpoints?.["start/finish"] || {})
                .filter(([key, value]) => /^loop\d+$/.test(key) && value?.time)
                .map(([key, value]) => [key, value.time])
            )
          : null)
        || {};
      const recorded = Object.keys(loopTimestamps)
        .map((k) => (/^loop(\d+)$/.test(k) ? parseInt(k.slice(4), 10) : 0))
        .reduce((mx, n) => (n > mx ? n : mx), 0);
      return Array.from({ length: recorded }, (_, i) => {
        const key = "loop" + (i + 1);
        const rawTime = loopTimestamps[key] || null;
        const m = toMoment(rawTime);
        return {
          name: "Loop " + (i + 1),
          dateStr: m?.isValid() ? m.format("MMM DD, YYYY") : null,
          timeStr: m?.isValid() ? m.format("HH:mm:ss") : null,
          hasData: m?.isValid() || false,
        };
      });
    }
    const cpData = data.checkpoints || {};
    const cpDefs = category?.checkpoints || [];
    const timeline = [];
    let finishNode = null;

    cpDefs.forEach((cp) => {
      if (cp.type === "startfinish") {
        const rawStart = cpData["start"]?.time || null;
        let mStart = toMoment(rawStart);
        // Chip missed at the start mat: no personal reading exists, so show the
        // category gun instead of an empty row. Marked isGun so it renders muted
        // and is never mistaken for a measured crossing.
        const isGunStart = !mStart?.isValid() && !!data.missedStart && !!category?.startTime;
        if (isGunStart) mStart = toMoment(category.startTime?.toDate ? category.startTime.toDate() : category.startTime);
        timeline.push({ name: "Start", dateStr: mStart?.isValid() ? mStart.format("MMM DD, YYYY") : null, timeStr: mStart?.isValid() ? mStart.format("HH:mm:ss") : null, hasData: mStart?.isValid() || false, isGun: isGunStart, ms: mStart?.isValid() ? mStart.valueOf() : null, isStart: true });

        const rawFinish = cpData["finish"]?.time || null;
        const mFinish = toMoment(rawFinish);
        // No finish crossing, but the operator has declared why: say DNF/DNS/DSQ
        // instead of a bare "-", which reads as missing data rather than a
        // decision. The runner asks "did the system lose my time?" — it did not.
        const declaredStatus = ["DNF", "DNS", "DSQ"].includes(status) ? status : null;
        finishNode = mFinish?.isValid()
          ? { name: "Finish", dateStr: mFinish.format("MMM DD, YYYY"), timeStr: mFinish.format("HH:mm:ss"), hasData: true, ms: mFinish.valueOf() }
          : { name: "Finish", dateStr: null, timeStr: declaredStatus, hasData: false, isStatus: !!declaredStatus };
      } else {
        const rawTime = cpData[cp.locationId]?.time || null;
        const m = toMoment(rawTime);
        const node = { name: cp.name, dateStr: m?.isValid() ? m.format("MMM DD, YYYY") : null, timeStr: m?.isValid() ? m.format("HH:mm:ss") : null, hasData: m?.isValid() || false, ms: m?.isValid() ? m.valueOf() : null };
        
        const isFinishMat =
          cp.type === "finish" || (cp.name || "").toLowerCase() === "finish";
        // A separate Start mat is chip-timed, exactly like the combined
        // start/finish mat above — it is not a hand-entered split. Dropping it
        // here left two-mat events showing only a Finish row, so the athlete
        // could not see the crossing their own time was measured from.
        const isStartMat =
          cp.type === "start" || (cp.name || "").toLowerCase() === "start";

        if (isFinishMat) {
          finishNode = node;
        } else if (isStartMat) {
          timeline.push({ ...node, isStart: true });
        } else if (!showSplits) {
          // Hand-entered split: drop it, keep Start and Finish (both chip-timed).
        } else {
          timeline.push(node);
        }
      }
    });

    if (finishNode) timeline.push(finishNode);

    // Race time at each crossing and the leg since the previous one, as a
    // runner reads their splits: elapsed from the gun (from their own start
    // for an individual-start category), and "from WS1" for the leg. A missed
    // crossing gets neither, and the next leg runs from the last one recorded.
    const gunMs = toMoment(category?.startTime?.toDate ? category.startTime.toDate() : category?.startTime)?.valueOf();
    const startNode = timeline.find((n) => n.isStart && n.ms != null);
    const baseMs = category?.individualStart ? startNode?.ms : (Number.isFinite(gunMs) ? gunMs : startNode?.ms);
    // A checkpoint with no time before one the runner was recorded at was
    // passed, not skipped: its officer missed them. Leaving an empty row there
    // reads as a hole in the timing, so it is left out and the next leg runs
    // from the last crossing that was recorded. Empty checkpoints after the
    // furthest one are where a runner still on course is heading, and stay.
    let lastRecorded = -1;
    timeline.forEach((n, i) => { if (n.hasData) lastRecorded = i; });
    const shown = timeline.filter((n, i) => n.hasData || n.isStatus || n.isGun || i > lastRecorded);
    let prev = null;
    return shown.map((n) => {
      if (n.ms == null || !n.hasData) return n;
      const out = {
        ...n,
        elapsed: baseMs != null && n.ms >= baseMs ? fmtSplit(n.ms - baseMs) : null,
        leg: prev && !n.isStart ? fmtSplit(n.ms - prev.ms) : null,
        legFrom: prev && !n.isStart ? prev.name : null,
      };
      prev = n;
      return out;
    });
  })();

  // A runner still out on course has no arrival or finish time yet, so the two
  // boxes that hold them read "-". Show how far they are instead.
  const onCourse = (() => {
    if (isLooping || status !== "ON RACE") return null;
    const at = lastCheckpoint(data, category);
    return at && !at.finished ? { name: at.name, raceTime: at.raceTime || "-" } : null;
  })();

  // The certificate reads "OFFICIAL FINISHER", so it belongs to runners who
  // actually finished inside the window. A DNF stopped, a DNS never started, a
  // DSQ was removed, and OVER COT crossed after the cut-off — issuing any of
  // them a finisher's certificate states something untrue.
  const certBlockReason = (() => {
    const st = String(data.status || runner.status || "").toUpperCase();
    if (st === "DNS") return "Did not start — no certificate";
    if (st === "DNF") return "Did not finish — no certificate";
    if (st === "DSQ") return "Disqualified — no certificate";
    if (data.isCOT || runner.isCOT || st === "COT") return "Over cut-off — no certificate";
    // No finish time means no finish, whatever the status field says. The
    // checks above read the status an operator typed; a runner whose chip was
    // read at the start and never at the line has none, so every one of them
    // fell through and was offered a finisher's certificate while this same
    // modal displayed DNF — that badge is derived from the missing finish,
    // not from the stored status.
    // A hand-timed (buzzer) result has an elapsed time and no crossing
    // instant, so finishTime is empty for every one of them; the recorded
    // duration is their finish.
    const hasDuration = Boolean(data.recordedDuration || runner.recordedDuration || (finishDuration && finishDuration !== "-"));
    if (!finishTime && !hasDuration) return "No finish time — no certificate";
    return null;
  })();
  const canDownloadCert = !!eventData?.enableCertificate && !certBlockReason;

  const generateCertificate = async (member = null) => {
    // Guard the renderer too, not just the button: a disabled control is a UI
    // affordance, and this is the function that actually produces the file.
    if (certBlockReason) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const size = resolveCertSize(eventData?.certificateLayout);
    const L = resolveCertLayout(eventData?.certificateLayout, size);
    const splits = checkpointTimeline.map((cp) => ({ checkpointName: cp.name, time: cp.timeStr || "-" }));
    canvas.width = size.w; canvas.height = size.h;
    const ctx = canvas.getContext("2d");

    // Uploaded typefaces must be registered before the first fillText, or the
    // whole sheet is drawn in the fallback font.
    await loadCertFonts(L.customFonts);

    ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Optional per-event artwork. Drawn first so every text block lands on top.
    // Failure is non-fatal: a broken URL should still produce a usable result.
    const bgUrl = eventData?.certificateBackground;
    if (bgUrl) {
      try {
        const img = await new Promise((resolve, reject) => {
          const im = new Image();
          im.crossOrigin = "anonymous";
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = bgUrl;
        });
        // Cover, not stretch. Forcing the artwork to the canvas box squashed any
        // background whose aspect ratio differed from the canvas — a landscape
        // A4 design came out vertically compressed, with warped logos and type.
        // Scale to fill, then centre and crop the overflow.
        const scale = Math.max(canvas.width / img.width, canvas.height / img.height);
        const dw = img.width * scale;
        const dh = img.height * scale;
        ctx.drawImage(img, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh);
      } catch {
        // keep the plain white base
      }
    }

    if (L.showHeaderBand && L.headerHeight > 0 && !bgUrl) {
      ctx.fillStyle = "#F3F4F6"; ctx.fillRect(0, 0, canvas.width, L.headerHeight);
      ctx.strokeStyle = "#E5E7EB"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(0, L.headerHeight); ctx.lineTo(canvas.width, L.headerHeight); ctx.stroke();
    }

    /**
     * Draw one text block honouring its alignment, font, and prefix/suffix.
     * Blocks default to `show: true`, so a layout stored before visibility was
     * per-block still draws everything it used to.
     */
    const drawBlock = (key, raw, total) => {
      const b = L[key];
      if (!b || b.show === false) return;
      const { x, align } = resolveCertX(b, canvas.width);
      ctx.textAlign = align;
      ctx.fillStyle = b.color;
      ctx.font = certFont(b, b.size);
      ctx.fillText(formatCertValue(raw, b, total), x, b.y);

      // Caption under the value (rank blocks), positioned by its own gap so it
      // tracks the number when the number moves.
      if (b.showLabel && b.label) {
        ctx.fillStyle = b.labelColor;
        ctx.font = `${b.labelSize}px ${certFamily(b.font)}`;
        // labelOffsetX lets a caption sit beside its value rather than under
        // it — artwork often places the wording next to an icon, metres away
        // from the number it describes.
        ctx.fillText(b.label, x + (b.labelOffsetX ?? 0), b.y + (b.labelGap ?? 40));
      }
    };

    // Raw values in; the block's own textCase decides capitalisation, so an
    // event whose artwork sets names in full caps is a setting, not a code edit.
    drawBlock("eventName", eventData?.eventName || "VIONIX EVENT");
    if (L.subtitle.text) drawBlock("subtitle", L.subtitle.text);
    // A relay row is a team, so `member` names whose certificate this is. The
    // team still owns the time and distance — only the name and BIB change, and
    // the team name moves onto the category line so the sheet still reads as a
    // team result.
    const certName = member?.name || data.name || runner.name || "";
    const certBib = member?.bib || data.bib || runner.bib || "-";
    const teamSuffix = member && (runner.name || data.name)
      ? "  |  " + (runner.name || data.name)
      : "";
    drawBlock("runnerName", certName);
    drawBlock("bibOnly", certBib);
    drawBlock("categoryOnly", catName);
    drawBlock("categoryBib", catName + "  |  BIB: " + certBib + teamSuffix);
    if (L.finishLabel.text) drawBlock("finishLabel", L.finishLabel.text);

    // A timed category has no per-runner finish time: everyone runs until the
    // cut-off, so what the mats recorded is just whenever each runner last
    // crossed before the horn — 23:58:11 against 23:59:58 reads as one being
    // faster when both ran the full 24 hours, and the smaller number looks
    // better. The race duration is the honest figure; distance is what
    // separates them, and that is published beside it.
    const asMs = (t) => {
      const v = t?.toDate ? t.toDate() : t;
      const ms = v ? new Date(v).getTime() : NaN;
      return Number.isFinite(ms) ? ms : null;
    };
    const isDeclared = ["DNF", "DNS", "DSQ"].includes(
      String(data.status || runner.status || "").toUpperCase(),
    );
    const timedDurationMs = (() => {
      if (!category || Number(category.targetLoops) > 0) return null;
      // Same guard as the results table: a lane length marks a timed race.
      // Posrunners sets neither targetLoops nor a lane, and its runners each
      // finish at their own lap with their own time.
      if (!(Number(category.lapDistanceMeters) > 0)) return null;
      const s = asMs(category.startTime);
      const c = asMs(category.cutOffTime);
      return s !== null && c !== null && c > s ? c - s : null;
    })();

    const rawFinish =
      timedDurationMs !== null && !isDeclared
        ? (() => {
            const t = Math.floor(timedDurationMs / 1000);
            const p = (n) => String(n).padStart(2, "0");
            return `${p(Math.floor(t / 3600))}:${p(Math.floor((t % 3600) / 60))}:${p(t % 60)}`;
          })()
        : finishDuration;

    const finishTimeDisplay = displayElapsed(rawFinish, category);
    drawBlock("finishTime", finishTimeDisplay);

    if (L.divider.show) {
      ctx.strokeStyle = L.divider.color; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(100, L.divider.y); ctx.lineTo(canvas.width - 100, L.divider.y); ctx.stroke();
    }

    // Legacy symmetric pair, still honoured for events laid out before the
    // ranks split into two independently placed blocks.
    if (L.ranks.show) {
      const offset = L.ranks.offset; const rankY = L.ranks.y;
      ctx.textAlign = "center"; ctx.fillStyle = L.ranks.color; ctx.font = `bold ${L.ranks.size}px Arial`;
      ctx.fillText(overallRank, canvas.width / 2 - offset, rankY);
      ctx.fillStyle = L.ranks.labelColor; ctx.font = "24px Arial";
      ctx.fillText("OVERALL RANK", canvas.width / 2 - offset, rankY + 40);
      ctx.fillStyle = L.ranks.color; ctx.font = `bold ${L.ranks.size}px Arial`;
      ctx.fillText(genderRank, canvas.width / 2 + offset, rankY);
      ctx.fillStyle = L.ranks.labelColor; ctx.font = "24px Arial";
      ctx.fillText("GENDER RANK", canvas.width / 2 + offset, rankY + 40);
    } else {
      drawBlock("overallRank", overallRank, overallOutOf);
      drawBlock("genderRank", genderRank, genderOutOf);
    }

    // Distance covered. On a timed race the clock reads the same for everyone,
    // so the distance IS the achievement — a 24H finisher's certificate should
    // say 181.35 km, not just 23:58:11. Drawn only when the category measures a
    // lane; the block defaults to hidden so road events are unaffected.
    // totalDistanceKm is computed by the results API, not stored on the result
    // document — and the live Firestore subscription replaces `data` with the
    // raw document, so the field is gone by the time the certificate renders.
    // Recompute from the parts, which both sources do carry.
    const lapMetres = Number(category?.lapDistanceMeters);
    const extra = Number(data.extraMeters ?? runner.extraMeters);
    const laps = Number(data.completedLoops ?? runner.completedLoops);
    const declared = ["DNF", "DNS", "DSQ"].includes(String(status || "").toUpperCase());
    // The API sends null for any category it cannot measure. Two traps here:
    // `??` passes an explicit null straight through, and Number(null) is 0 —
    // not NaN — so a fixed-distance runner printed 0.00 instead of nothing.
    // Test the raw value, never the coercion.
    const apiKm =
      typeof runner.totalDistanceKm === "number" &&
      Number.isFinite(runner.totalDistanceKm)
        ? runner.totalDistanceKm
        : null;
    // A fixed-distance category has no measured extra: everyone stops on the
    // line, so the distance is the one the category is named for — a 50K
    // finisher covered 50 km whatever the lane measured. Timed categories are
    // the opposite: the clock is shared, so the distance has to be computed.
    const officialKm = (() => {
      if (!(Number(category?.targetLoops) > 0)) return null;
      const m = /(\d+(?:[.,]\d+)?)\s*K/i.exec(String(category?.name || ""));
      return m ? Number(m[1].replace(",", ".")) : null;
    })();

    const distanceKm =
      apiKm !== null
        ? apiKm
        : declared
          ? null
          : officialKm !== null
            ? officialKm
            : lapMetres > 0 && Number.isFinite(laps)
              ? (laps * lapMetres + (Number.isFinite(extra) ? extra : 0)) / 1000
              : null;
    if (distanceKm !== null && Number.isFinite(distanceKm)) {
      // Two decimals only where they carry information. A timed runner stopped
      // at 181.35 km and the fraction is the achievement; a 100K finisher
      // covered exactly 100, and "100.00" just looks like a rounding artefact.
      const printed = Number.isInteger(distanceKm)
        ? String(distanceKm)
        : distanceKm.toFixed(2);
      drawBlock("distance", printed);
    }

    if (L.splits.show && splits.length > 0) {
      let curY = L.splits.y;
      const tw = L.splits.width; const tx = (canvas.width - tw) / 2;
      ctx.textAlign = "left"; ctx.fillStyle = L.splits.labelColor; ctx.font = `bold 24px ${certFamily(L.splits.font)}`;
      ctx.fillText("SPLITS", tx, curY); curY += 20;
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = L.splits.labelColor; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(tx, curY); ctx.lineTo(tx + tw, curY); ctx.stroke();
      ctx.restore();
      curY += 60;
      const nc = Math.ceil(splits.length / 4);
      const cw = (tw - (nc - 1) * 40) / nc;
      let sx = tx;
      for (let c = 0; c < nc; c++) {
        let ry = curY;
        splits.slice(c * 4, (c + 1) * 4).forEach((cp) => {
          ctx.textAlign = "left"; ctx.font = `${L.splits.size}px ${certFamily(L.splits.font)}`; ctx.fillStyle = L.splits.labelColor;
          ctx.fillText(cp.checkpointName, sx + 10, ry);
          ctx.textAlign = "right"; ctx.font = `bold ${L.splits.size}px ${certFamily(L.splits.font)}`; ctx.fillStyle = L.splits.color;
          ctx.fillText(cp.time, sx + cw - 10, ry);
          // Row hairline. Tinted from the split colour rather than fixed grey so
          // it stays visible on dark artwork, but drawn faint — at full strength
          // it reads as a table border and overwhelms the times above it.
          ctx.save();
          ctx.globalAlpha = 0.25;
          ctx.strokeStyle = L.splits.labelColor; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.moveTo(sx, ry + 15); ctx.lineTo(sx + cw, ry + 15); ctx.stroke();
          ctx.restore();
          ry += 50;
        });
        sx += cw + 40;
      }
    }

    if (L.footer.show) {
      ctx.fillStyle = L.footer.bgColor; ctx.fillRect(0, canvas.height - 80, canvas.width, 80);
      ctx.textAlign = "center"; ctx.fillStyle = L.footer.color; ctx.font = "bold 24px Arial";
      ctx.fillText(L.footer.text, canvas.width / 2, canvas.height - 30);
    }

    const link = document.createElement("a");
    link.download =
      (member?.name || data.name || runner.name) +
      "-Result-" +
      (member?.bib || data.bib || runner.bib) +
      ".png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const isM = data.gender?.toLowerCase() === "male" || data.gender?.toLowerCase() === "m";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      {/* The card never scrolls as a whole: its sections keep their size and only
          the checkpoint list scrolls inside the space left, so a 10-CP ultra
          does not push the name and times off screen. */}
      <div className="relative bg-white rounded-card w-full max-w-lg md:max-w-xl mx-auto max-h-[92vh] flex flex-col overflow-hidden shadow-2xl [&>*]:shrink-0" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-4 pt-5 pb-2">
          {/* Smaller on a phone: a full Indonesian name is long enough to wrap
              onto a second line at text-2xl, which shifted everything below it. */}
          <h2 className="text-lg sm:text-2xl font-bold text-slate-900 leading-tight flex-1 min-w-0 pr-3">
            {capitalizeWords(data.name || runner.name || "-")}
          </h2>
          <div className="shrink-0">
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors">
              <FontAwesomeIcon icon={faTimes} className="text-sm" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 px-4 pb-4 flex-wrap">
          {/* A team has no single BIB or gender: show each runner instead. */}
          {runner.isTeam ? (
            (runner.members || []).map((member) => (
              <span key={member.bib} className="inline-flex items-center text-xs text-slate-600 border border-slate-200 rounded-full px-3 py-1 min-h-[26px]">
                <span className="tabular-nums text-slate-400 mr-1.5">{member.bib}</span>
                {capitalizeWords(member.name) || "-"}
              </span>
            ))
          ) : (
            <>
              <span className="inline-flex items-center text-xs text-slate-600 border border-slate-200 rounded-full px-3 py-1 min-h-[26px]">BIB {data.bib || runner.bib || "-"}</span>
              <span className="inline-flex items-center gap-1 text-xs text-slate-600 border border-slate-200 rounded-full px-3 py-1 min-h-[26px]">
                <img src={"https://flagcdn.com/w40/" + flagCode + ".png"} onError={(e) => { e.target.src = "https://flagcdn.com/w40/un.png"; }} className="w-4 h-3 object-cover border border-slate-200 rounded" alt={nationalityDisplay} />
                {nationalityDisplay}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-slate-600 border border-slate-200 rounded-full px-3 py-1 min-h-[26px]">
                <FontAwesomeIcon icon={isM ? faMars : faVenus} />
                {isM ? "Male" : "Female"}
              </span>
            </>
          )}
          {/* The status closes the row, pushed to its right edge. */}
          <span className={"ml-auto inline-flex items-center text-xs border rounded-full px-3 py-1 min-h-[26px] " + (status === "FINISHED" ? "bg-green-50 text-green-700 border-green-200" : status === "COT" || status === "DSQ" ? "bg-red-50 text-red-600 border-red-200" : status === "ON RACE" ? "bg-blue-50 text-blue-700 border-blue-200" : status === "DNF" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200")}>
            {/^(DNF|DNS|DSQ|COT)$/.test(status) ? status : capitalizeWords(status)}
          </span>
        </div>
        <div className="mx-4 mb-4 border border-slate-200 rounded-card overflow-hidden">
          <div className="grid grid-cols-2 sm:grid-cols-3">
            <div className="col-span-2 sm:col-span-1 px-3 py-3 text-center border-b sm:border-b-0 sm:border-r border-slate-200"><p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Category</p><p className="text-sm font-bold text-slate-900 leading-tight">{catName}</p></div>
            {!isLooping && data.gunTime ? (
              <>
                <div className="px-3 py-3 text-center border-r border-slate-200"><p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Net Time{data.netTimeSource === "gun" ? " *" : ""}</p><p className="text-sm font-bold text-slate-900 tabular-nums">{((data.missedStart && !data.netTime) ? "-" : (data.netTime || data.gunTime || "-")).replace(/\.\d+$/, "")}</p></div>
                <div className="px-3 py-3 text-center"><p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Gun Time</p><p className="text-sm font-bold text-slate-900 tabular-nums">{data.gunTime.replace(/\.\d+$/, "")}</p></div>
              </>
            ) : onCourse ? (
              <>
                <div className="px-3 py-3 text-center border-r border-slate-200"><p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Last Checkpoint</p><p className="text-sm font-bold text-slate-900 uppercase leading-tight">{onCourse.name}</p></div>
                <div className="px-3 py-3 text-center"><p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Race Time</p><p className="text-sm font-bold text-slate-900 tabular-nums">{onCourse.raceTime}</p></div>
              </>
            ) : (
              <>
                <div className="px-3 py-3 text-center border-r border-slate-200"><p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Arrival</p><p className="text-sm font-bold text-slate-900 tabular-nums">{arrivalTimeDisplay}</p></div>
                <div className="px-3 py-3 text-center"><p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Finish Time</p><p className="text-sm font-bold text-slate-900 tabular-nums">{displayElapsed(finishDuration, category)}</p></div>
              </>
            )}
          </div>
          <div className="flex border-t border-slate-200 divide-x divide-slate-200">
            <div className="flex-1 px-3 py-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Overall</p>
              <p className="text-2xl font-bold text-slate-900">
                {overallRank}
                {overallOutOf > 0 && (
                  <span className="text-sm font-medium text-slate-400"> / {overallOutOf}</span>
                )}
              </p>
            </div>
            {/* A mixed team has no gender podium; show its size instead. */}
            {runner.isTeam ? (
              <div className="flex-1 px-3 py-3 text-center">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Runners</p>
                <p className="text-2xl font-bold text-slate-900">
                  {runner.memberCount ?? runner.members?.length ?? "-"}
                </p>
              </div>
            ) : (
              <div className="flex-1 px-3 py-3 text-center">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Gender Rank</p>
                <p className="text-2xl font-bold text-slate-900">
                  {genderRank}
                  {genderOutOf > 0 && (
                    <span className="text-sm font-medium text-slate-400"> / {genderOutOf}</span>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="px-4 mb-4">
          {/* A relay team has no single recipient: five runners each earned the
              result, and a certificate naming only the first BIB leaves four
              with nothing. One button per member, each producing that runner's
              own sheet with the team's time and distance. */}
          {runner?.isTeam && (runner.members || []).length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Certificate per runner
              </p>
              {(runner.members || []).map((m) => (
                <Button
                  key={m.bib}
                  onClick={
                    canDownloadCert ? () => generateCertificate(m) : undefined
                  }
                  disabled={!canDownloadCert}
                  color="teal"
                  fullWidth
                >
                  <FontAwesomeIcon icon={faFileLines} />
                  {capitalizeWords(m.name || "")} · {m.bib}
                </Button>
              ))}
            </div>
          ) : (
            <Button
              onClick={canDownloadCert ? () => generateCertificate() : undefined}
              disabled={!canDownloadCert}
              color="teal"
              fullWidth
            >
              <FontAwesomeIcon icon={faFileLines} />
              {/* The reason (no finish, DNF, over cut-off…) stays internal; a runner
                  only needs to know there is no certificate. */}
              {canDownloadCert ? "Certificate" : "Certificate Not Available"}
            </Button>
          )}
        </div>
        {checkpointTimeline.length > 0 && (
          <div className={`mx-4 mb-5 border border-slate-200 rounded-card overflow-hidden flex flex-col ${checkpointOpen ? "!shrink min-h-[7rem]" : ""}`}>
            <button className="w-full flex items-center justify-between px-4 py-3 shrink-0" onClick={() => setCheckpointOpen((v) => !v)}>
              <div className="text-left">
                <p className="text-xs font-black text-slate-900 uppercase tracking-wider">Checkpoint</p>
                {finishMoment?.isValid() && (
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className="text-[11px] text-slate-500">Finish</span>
                    <span className="text-slate-300 text-[11px]">|</span>
                    <span className="text-[11px] text-slate-500">{finishMoment.format("MMM DD, YYYY")}</span>
                    <span className="text-slate-300 text-[11px]">|</span>
                    <span className="text-[11px] font-semibold text-slate-700">{arrivalTimeDisplay}</span>
                  </div>
                )}
              </div>
              <FontAwesomeIcon icon={checkpointOpen ? faChevronUp : faChevronDown} className="text-slate-400 text-xs shrink-0" />
            </button>
            {checkpointOpen && (
              <ul className="border-t border-slate-200 min-h-0 overflow-y-auto overscroll-contain">
                {checkpointTimeline.map((cp, idx) => {
                  const isFirst = idx === 0;
                  const isLast = idx === checkpointTimeline.length - 1;
                  return (
                    <li key={idx} className="flex items-stretch">
                      <div className="w-28 shrink-0 flex flex-col justify-center items-end text-right pr-3 py-3 pl-4">
                        <p className="text-[10px] text-slate-400 leading-tight">{cp.hasData ? cp.dateStr : ""}</p>
                        <p className={"text-xs font-semibold leading-tight " + (cp.isStatus ? "text-amber-600" : "text-slate-600")}>
                          {cp.hasData || cp.isStatus ? cp.timeStr : "-"}
                        </p>
                      </div>
                      <div className="flex flex-col items-center w-5 shrink-0">
                        <div className={"w-px flex-1 " + (isFirst ? "bg-transparent" : "bg-slate-200")} />
                        <div className={"w-2.5 h-2.5 rounded-full shrink-0 z-10 " + (cp.isStatus ? "bg-amber-500" : cp.isGun ? "bg-slate-300" : cp.hasData ? "bg-green-500" : "bg-slate-200")} />
                        <div className={"w-px flex-1 " + (isLast ? "bg-transparent" : "bg-slate-200")} />
                      </div>
                      <div className="flex-1 flex items-center justify-between gap-3 pl-3 py-3 pr-4">
                        <p className={"text-xs font-semibold uppercase tracking-wide " + (cp.isGun ? "text-slate-400" : cp.hasData ? "text-slate-900" : "text-slate-400")}>
                          {cp.name}
                        </p>
                        {(cp.elapsed || cp.leg) && (
                          <div className="text-right shrink-0">
                            {cp.elapsed && <p className="text-xs font-bold text-slate-900 tabular-nums leading-tight">{cp.elapsed}</p>}
                            {cp.leg && (
                              <p className="text-[10px] text-slate-400 leading-tight mt-0.5">
                                <span className="tabular-nums font-semibold text-slate-500">{cp.leg}</span> from {cp.legFrom}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
};
